import { Grid, Text, Stack, Group, Card, Modal, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { CertificateRequestStatus } from "@tfg/db/browser"
import { AreYouSureModal, Button, CopyButton, DateCell, EqualWidthGrid } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import {
  findCertificateRequestsForSupplierQueryKey,
  FindCertificateRequestsForSupplierResponseDto,
  useDeleteCertificateRequest,
  useUpdateManyCertificateRequests,
} from "~/gen/api"
import { CertificateRequestStatusBadge } from "~/pages/Brand/Certificates/components/CertificateRequestStatusBadge"
import { useCertificateRequestsBatchModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { CertificateRequestBatchPopover } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestBatchPopover"
import { CertificateRequestProductionSite } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestProductionSite"
import { CertificateRequestPurchaseOrder } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateRequestPurchaseOrder"
import { CertificatesSelect } from "~/pages/SupplierPortal/TraceabilityRequests/components/CertificateSelect"
import { useAuth } from "~/store/auth-context.tsx"
import { showAxiosError } from "~/utils/showAxiosError"
import { useGetOperations } from "~/utils/operations"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  certificateRequest: FindCertificateRequestsForSupplierResponseDto
  setTransferCertificateRequestIds: (ids: string[] | undefined) => void
  openTransferModal: () => void
}

export const CertificateRequest = ({
  certificateRequest,
  setTransferCertificateRequestIds,
  openTransferModal,
}: Props) => {
  const { isAdmin } = useAuth()
  const displayedCertificateRequest = certificateRequest
  const [certificateId, setCertificateId] = useState<string | null>(
    displayedCertificateRequest.certificateId
  )

  //Necessary for live update after query invalidation in batch update
  useEffect(() => {
    setCertificateId(displayedCertificateRequest.certificateId)
  }, [displayedCertificateRequest.certificateId])

  const { t } = useTranslation(["traceability"])
  const activity = useGetOperations()[displayedCertificateRequest.activityName]

  const { mutate: updateCertificateRequest } = useUpdateManyCertificateRequests(
    {
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: findCertificateRequestsForSupplierQueryKey(),
          })
        },
      },
    }
  )
  const { mutateAsync: deleteCertificateRequest } = useDeleteCertificateRequest()

  const handleSubmit = () => {
    updateCertificateRequest({
      data: {
        ids: [certificateRequest.id, ...certificateRequest.aggregatedIds],
        update: {
          status: "SUBMITTED",
        },
      },
    })
  }

  const handleUpdate = (newCertificateId: string | null) => {
    updateCertificateRequest({
      data: {
        ids: [certificateRequest.id, ...certificateRequest.aggregatedIds],
        update: {
          certificateId: newCertificateId,
        },
      },
    })
  }

  const [
    openedDeleteModal,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const productionSiteId = displayedCertificateRequest.productionSite?.id

  const [opened, { close: closePopover, open: openPopover }] =
    useDisclosure(false)
  const [
    openedUploadModal,
    { open: openUploadModal, close: closeUploadModal },
  ] = useDisclosure(false)

  const { openBatchModal } = useCertificateRequestsBatchModalContext()

  const closeUploadFlow = (): void => {
    closePopover()
    closeUploadModal()
  }

  const handleDeleteCertificateRequests = async (): Promise<void> => {
    const certificateRequestIds = [
      certificateRequest.id,
      ...certificateRequest.aggregatedIds,
    ]
    const deleteResults = await Promise.allSettled(
      certificateRequestIds.map(async (id) =>
        deleteCertificateRequest({
          data: {
            certificateRequestId: id,
          },
        })
      )
    )

    const failedDeleteResult = deleteResults.find(
      (result) => result.status === "rejected"
    )

    if (failedDeleteResult) {
      const reason =
        failedDeleteResult.reason instanceof Error
          ? failedDeleteResult.reason
          : new Error("Delete certificate request failed")
      showAxiosError({
        error: reason,
        showNotification: true,
        notificationTitle: t("certificates.actions.delete"),
      })
      return
    }

    await queryClient.invalidateQueries({
      queryKey: findCertificateRequestsForSupplierQueryKey(),
    })
    showSuccessNotification({
      title: t("certificates.actions.delete"),
    })
  }

  return (
    <>
      <Card
        w="100%"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid pos="relative" style={{ flexGrow: 1 }}>
          <Grid.Col span={12}>
            <Stack gap="var(--tfg-molecule-padding-y)">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "4fr 1fr",
                  gap: "var(--mantine-spacing-sm)",
                  alignItems: "start",
                }}
              >
                <Title order={3} size="h5">
                  {displayedCertificateRequest.type.name}
                </Title>
                <Group justify="flex-end" wrap="nowrap">
                  <CertificateRequestStatusBadge
                    certificateRequestStatus={
                      displayedCertificateRequest.status
                    }
                    userType="supplier"
                  />
                </Group>
              </div>
              {!!displayedCertificateRequest.rejectionMessage &&
                displayedCertificateRequest.status ===
                  CertificateRequestStatus.REJECTED && (
                  <Text c="red">
                    {displayedCertificateRequest.rejectionMessage}
                  </Text>
                )}
              <CertificateRequestProductionSite
                productionSite={displayedCertificateRequest.productionSite}
              />
              {displayedCertificateRequest.type.category === "TRANSACTION" && (
                <CertificateRequestPurchaseOrder
                  certificateRequest={displayedCertificateRequest}
                />
              )}
              <Group>
                <Text fw={600}>{t("certificates.activityName")}</Text>
                <Group gap="xs">
                  {activity.icon && <activity.icon size={20} />}
                  <Text>{activity.name}</Text>
                </Group>
              </Group>
              <Group>
                <Text fw={600}>{t("certificates.labels.maxIssueDate")}:</Text>
                <DateCell date={displayedCertificateRequest.maxIssueDate} />
              </Group>
            </Stack>
          </Grid.Col>
        </Grid>
        {displayedCertificateRequest.status === "OPENED" && (
          <Stack mt="auto" pt="sm" gap="sm">
            <EqualWidthGrid>
              <Button onClick={openUploadModal} fullWidth={true}>
                {t("certificates.upload.title")}
              </Button>
              <Button
                variant="secondary"
                fullWidth={true}
                onClick={() => {
                  setTransferCertificateRequestIds([
                    displayedCertificateRequest.id,
                    ...displayedCertificateRequest.aggregatedIds,
                  ])
                  openTransferModal()
                }}
              >
                {t("certificates.actions.transfer")}
              </Button>
            </EqualWidthGrid>
            {isAdmin && (
              <EqualWidthGrid>
                <Button
                  variant="admin"
                  fullWidth={true}
                  onClick={() => {
                    openDeleteModal()
                  }}
                >
                  {t("certificates.actions.delete")}
                </Button>
                <CopyButton
                  variant={"admin-tertiary"}
                  title="Copy ID"
                  copyableText={displayedCertificateRequest.id}
                  fullWidth={true}
                />
              </EqualWidthGrid>
            )}
          </Stack>
        )}
      </Card>
      <AreYouSureModal
        opened={openedDeleteModal}
        onClose={closeDeleteModal}
        title={t("certificates.actions.delete")}
        areYouSureText={t("certificates.modal.areyousure")}
        cancelText={t("certificates.modal.no")}
        confirmText={t("certificates.modal.yes")}
        onConfirm={() => {
          void handleDeleteCertificateRequests()
        }}
      />
      <Modal
        opened={openedUploadModal}
        onClose={closeUploadFlow}
        title={t("certificates.upload.title")}
      >
        <Stack gap="sm">
          <CertificateRequestBatchPopover
            target={
              <Stack>
                <CertificatesSelect
                  certificateId={certificateId}
                  productionSiteId={productionSiteId}
                  onCertificateSelected={(newCertificateId) => {
                    setCertificateId(newCertificateId)

                    handleUpdate(newCertificateId)

                    if (
                      newCertificateId &&
                      displayedCertificateRequest.type.category ===
                        "TRANSACTION"
                    ) {
                      openPopover()
                    }
                  }}
                  typeId={displayedCertificateRequest.type.id}
                  disabled={displayedCertificateRequest.status !== "OPENED"}
                />
              </Stack>
            }
            onClick={() => {
              if (certificateId) {
                closePopover()
                openBatchModal({
                  certificateId,
                  certificateRequestId: displayedCertificateRequest.id,
                })
              }
            }}
            opened={opened}
            close={closePopover}
          />
          <Group justify="right">
            <Button
              disabled={!certificateId}
              onClick={() => {
                handleSubmit()
                closeUploadFlow()
              }}
            >
              {t("certificates.actions.submit")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
